import { TabsContent } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CHECKOUT_TEMPLATES,
  CheckoutTemplateId,
  checkoutTemplateLabel,
  isCheckoutTemplate,
} from '@/utils/checkoutTemplates'

type LayoutTabProps = {
  template: CheckoutTemplateId
  onSelect: (template: CheckoutTemplateId) => void
}

const LAYOUTS = CHECKOUT_TEMPLATES

function Bar({ className }: { className?: string }) {
  return <div className={cn('h-1.5 rounded-[2px] bg-zinc-500', className)} />
}

function Plate({ className, children }: { className?: string; children?: ReactNode }) {
  return (
    <div className={cn('rounded-[3px] bg-zinc-600/90', className)}>{children}</div>
  )
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div aria-hidden className="flex h-[72px] flex-col justify-center rounded-md bg-zinc-800 p-2">
      {children}
    </div>
  )
}

function LayoutSketch({ layout }: { layout: CheckoutTemplateId }) {
  if (layout === 'default') {
    return (
      <Frame>
        <div className="space-y-1.5">
          <Bar className="h-2 w-2/5 bg-zinc-400" />
          <Bar />
          <Bar className="w-4/5" />
          <Plate className="h-2.5" />
        </div>
      </Frame>
    )
  }

  if (layout === 'farol') {
    return (
      <Frame>
        <div className="grid grid-cols-2 gap-1.5">
          <div className="space-y-1.5">
            <Bar />
            <Bar />
            <Bar className="w-3/4" />
          </div>
          <div className="space-y-1.5">
            <Plate className="h-6" />
            <Plate className="h-2.5 bg-zinc-500" />
          </div>
        </div>
      </Frame>
    )
  }

  if (layout === 'meridiano') {
    return (
      <Frame>
        <div className="grid grid-cols-[1.15fr_0.85fr] gap-1.5">
          <div className="space-y-1.5">
            <Bar />
            <Bar />
            <Plate className="h-2.5" />
          </div>
          <div className="space-y-1.5 rounded-[3px] bg-zinc-700/70 p-1">
            <Bar className="w-2/3 bg-zinc-500" />
            <Bar className="bg-zinc-500" />
            <Bar className="w-1/2 bg-zinc-500" />
          </div>
        </div>
      </Frame>
    )
  }

  if (layout === 'agora') {
    return (
      <Frame>
        <div className="grid h-full grid-cols-[1.2fr_0.8fr] gap-1.5">
          <div className="space-y-1.5">
            <Bar />
            <Bar />
            <Plate className="h-2.5" />
          </div>
          <div className="space-y-1.5 rounded-[3px] bg-zinc-700 p-1">
            <Plate className="mx-auto h-5 w-7 bg-zinc-500" />
            <Bar className="bg-zinc-500" />
            <Bar className="w-2/3 bg-zinc-500" />
          </div>
        </div>
      </Frame>
    )
  }

  if (layout === 'insignia') {
    return (
      <Frame>
        <div className="grid grid-cols-[1.15fr_0.85fr] gap-1.5">
          <div className="space-y-1.5 rounded-[3px] border border-zinc-600 p-1">
            <Bar />
            <Bar />
            <Plate className="h-2" />
          </div>
          <div className="space-y-1.5 pt-0.5">
            <Plate className="mx-auto h-5 w-8" />
            <Bar />
            <Bar className="w-2/3" />
          </div>
        </div>
      </Frame>
    )
  }

  if (layout === 'prumo') {
    return (
      <Frame>
        <Bar className="mb-1.5 h-1 bg-zinc-600" />
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Bar />
            <Bar />
            <div className="grid grid-cols-3 gap-1">
              <div className="h-2.5 rounded-[2px] border border-zinc-400" />
              <div className="h-2.5 rounded-[2px] border border-zinc-600" />
              <div className="h-2.5 rounded-[2px] border border-zinc-600" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Bar className="w-3/4" />
            <Bar className="w-1/2" />
            <Plate className="h-2.5 bg-zinc-400" />
          </div>
        </div>
      </Frame>
    )
  }

  return (
    <Frame>
      <div className="grid grid-cols-[1.45fr_0.7fr] gap-1.5">
        <div className="space-y-1.5 rounded-[3px] bg-zinc-700/50 p-1">
          <Bar />
          <div className="grid grid-cols-2 gap-1">
            <Plate className="h-2.5" />
            <div className="h-2.5 rounded-[3px] border border-zinc-500" />
          </div>
          <Plate className="h-2" />
        </div>
        <div className="space-y-1.5 pt-0.5">
          <Plate className="mx-auto h-5 w-6" />
          <Bar />
          <Bar className="w-3/4" />
        </div>
      </div>
    </Frame>
  )
}

export function LayoutTab({ template, onSelect }: LayoutTabProps) {
  const { t } = useTranslation(['checkouts'])
  const selectedTemplate = isCheckoutTemplate(template) ? template : 'default'
  const buttonRefs = useRef<Partial<Record<CheckoutTemplateId, HTMLButtonElement | null>>>({})

  const choose = (next: CheckoutTemplateId) => {
    onSelect(next)
    buttonRefs.current[next]?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight'
    const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft'
    if (!forward && !backward) return
    event.preventDefault()
    const index = LAYOUTS.indexOf(selectedTemplate)
    const delta = forward ? 1 : -1
    const next = LAYOUTS[(index + delta + LAYOUTS.length) % LAYOUTS.length]
    choose(next)
  }

  return (
    <TabsContent value="layout" className="mt-8">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-xs font-medium uppercase text-zinc-400">
          {t('checkouts:template_title')}
        </h2>
      </div>
      <p className="mb-4 max-w-xl text-sm leading-5 text-muted">
        {t('checkouts:template_hint')}
      </p>
      <div
        role="radiogroup"
        aria-label={t('checkouts:template_title')}
        onKeyDown={onKeyDown}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        {LAYOUTS.map((layout) => {
          const selected = selectedTemplate === layout
          return (
            <button
              key={layout}
              ref={(node) => {
                buttonRefs.current[layout] = node
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              data-cy={`checkout-template-${layout}`}
              onClick={() => choose(layout)}
              className={cn(
                'flex min-h-11 w-full flex-col gap-2.5 rounded-xl border bg-zinc-900 p-2.5 text-left',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'motion-safe:transition-colors motion-safe:duration-150',
                selected ? 'border-primary' : 'border-zinc-800 hover:border-zinc-600',
              )}
            >
              <span className="relative block">
                <LayoutSketch layout={layout} />
                {selected ? (
                  <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-primary">
                    <Check size={12} strokeWidth={3} className="text-black" aria-hidden />
                  </span>
                ) : null}
              </span>
              <span className="flex flex-col gap-0.5 px-0.5 pb-0.5">
                <span className="text-sm font-semibold leading-5 text-white">
                  {checkoutTemplateLabel(layout)}
                </span>
                <span className="text-xs font-normal leading-4 text-zinc-400">
                  {t(`checkouts:template_${layout}_description`)}
                </span>
              </span>
            </button>
          )
        })}
      </div>
    </TabsContent>
  )
}
